/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";
import { tableFieldsAtomFamily_SUSPENDS } from "@zapier/canvas-state";

import { JotaiTool, JotaiToolExecute } from "../../index";

export const GetTableByIdParams = z.object({
  table_id: z
    .string()
    .describe(
      "The unique identifier for the table you want to retrieve details for",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Getting table info...' or 'Loading table data...'",
    ),
});

export type GetTableByIdParamsType = z.infer<typeof GetTableByIdParams>;

export const getTableByIdExecute: JotaiToolExecute<GetTableByIdParamsType> =
  atom(null, async (get, _set, { args }) => {
    let params: GetTableByIdParamsType;
    try {
      params = GetTableByIdParams.parse(args || {});
    } catch (error) {
      console.error("Error parsing arguments for getTableById:", error);
      return {
        $error: {
          message: `Error: Invalid arguments provided for get_table_by_id. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      };
    }

    const { table_id } = params;

    const tableFieldsResult = await get(
      tableFieldsAtomFamily_SUSPENDS(table_id),
    );

    try {
      if (!tableFieldsResult) {
        return {
          $error: {
            message: `Error: Table fields not found for table_id: ${table_id}. Details: ${JSON.stringify(tableFieldsResult)}`,
          },
        };
      }
      try {
        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(tableFieldsResult.data, null, 2),
            },
          ],
        };
      } catch (e) {
        console.error(
          `[GET_TABLE_BY_ID_TOOL] Error parsing successful table fields result as JSON. Error: ${e instanceof Error ? e.message : String(e)}`,
        );
        throw new Error(
          `Proxy returned success status but its response body was not valid JSON.`,
        );
      }
    } catch (error) {
      console.error(
        "[GET_TABLE_BY_ID_TOOL] Error in getTableByIdExecute:",
        error,
      );
      return {
        content: [
          {
            type: "text",
            text: `Error fetching table: ${error instanceof Error ? error.message : String(error)}`,
          },
        ],
      };
    }
  });

export const getTableByIdDescription = `Retrieves the detailed field configuration of a specific table by its ID.
This tool provides information about all the fields in a table, including their names, types, and other metadata.

IMPORTANT:
- The output includes detailed information about each field in the table.
- This information is useful for understanding table structure and for creating or updating table records.
- Field information includes field types, names, and other configuration details.

Required parameters:
- table_id: The ID of the table to fetch field information for.
    - If the user mentions a table by name on the canvas, try to find its ID in 'additionalData.canvas.nodes' (look for a node with a matching label and use its 'data.tableId' or 'data.table_id' property).
    - If the Table ID is directly provided or known, use that.

Output:
- The tool returns a JSON object representing the table's field configuration.
- This includes all field definitions with their types, names, and metadata.`;

export const getTableByIdAtom = atom<
  JotaiTool<z.infer<typeof GetTableByIdParams>>
>(() => ({
  name: "get_table_by_id",
  description: getTableByIdDescription,
  parameters: GetTableByIdParams,
  execute: getTableByIdExecute,
}));
getTableByIdAtom.debugLabel = "getTableByIdAtom";
/* eslint-enable sort-exports/sort-exports */
